import { useAuthQuery } from '@/api/index.ts';
import { studyQueryKeys } from '@/config/queryKeys';
import { canUserEditStudy } from '@/config/studies';
import { BroadscaleSamplesDetailTable } from '@/pages/Study/components/BroadscaleSamplesDetailTable';
import { StudyInfoSection } from '@/pages/Study/components/StudyInfoSection';
import { StudyTitleBar } from '@/pages/Study/components/StudyTitleBar';
import { fetchStudy } from '@/services/studies';
import { useAppStore } from '@/stores/app';
import { Study } from '@/types';
import { FullLoading } from '@components/index';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { useSuspenseQuery } from '@tanstack/react-query';
import { ErrorComponent, createLazyFileRoute } from '@tanstack/react-router';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import studyStyles from '../../pages/Studies/styles.module.css';
import '../../styles/components/ag-grid-overrides.css';
import detailsPageStyles from '../../styles/components/details.page.module.css';
import pageStyles from '../../styles/pages/index.module.css';

export const Route = createLazyFileRoute('/studies/$studyId_/broadscale-samples')({
  component: RouteComponent,
  errorComponent: e => <ErrorComponent {...e} />,
  pendingComponent: () => {
    const { t } = useTranslation('studies');
    return <FullLoading label={`${t('fetching')}...`} />;
  },
});

function RouteComponent() {
  const { t } = useTranslation('studies');
  // Mirror the loader's fallback: selectedOrganization is not persisted and is undefined
  // on the first render after a hard reload. selectedOrgStub IS persisted (localStorage,
  // synchronous), so it's available immediately. Without the fallback, selectedOrg is null
  // on render 1 (early return, fewer hooks) then non-null on render 2 — violating the
  // Rules of Hooks.
  const selectedOrg = useAppStore(useShallow(state => state.selectedOrganization ?? state.selectedOrgStub));
  const { studyId } = Route.useParams();
  const orgId = selectedOrg?.id ?? '';

  const { data, isLoading } = useSuspenseQuery<Study>({
    queryKey: studyQueryKeys.detail(orgId, studyId),
    queryFn: () => fetchStudy(orgId, studyId),
  });

  const { data: currentUser } = useAuthQuery();
  const canEdit = canUserEditStudy(data, currentUser ?? null);

  if (!selectedOrg?.id) {
    return <ErrorComponent error={new Error(t('study.no_org_selected'))} />;
  }

  const emptyTxt = t('empty', { ns: 'general' });

  const broadscaleAssay = data?.assays?.find(a => a.assay_type === 'broadscale');

  return (
    <Stack as="section" className={studyStyles.container} fullWidth orientation="vertical">
      <StudyTitleBar
        canEdit={canEdit}
        data={data}
        detailTitle={t('study.broadscaleDetailTitle')}
        orgId={orgId}
        pageStyles={pageStyles}
        studyStyles={studyStyles}
        t={t}
      />
      <Stack as="header" className={detailsPageStyles.header}>
        <StudyInfoSection data={data} emptyTxt={emptyTxt} isLoading={isLoading} t={t} />
      </Stack>

      <Stack fullWidth gap={4} orientation="vertical">
        <Stack alignItems="center" fullWidth justifyContent="space-between" orientation="horizontal">
          <Text as="h3">{t('study.broadscaleDetailTitle')}</Text>
          <Stack gap={4} orientation="horizontal">
            <Text weight="bold">Total:</Text>
            <Text>{broadscaleAssay?.num_samples ?? 0}</Text>
            {/* The Proteins Quantified stat is removed: `num_proteins` was dropped for good. It
                is the broadscale counterpart to num_proteoform_groups (PRQ-132), and there is no
                broadscale data or pipeline yet, so it had nothing to display. */}
          </Stack>
        </Stack>
        {/* `assay_id` is a required field on the search request, so there is nothing to query
            until the study's broadscale assay is known. */}
        {broadscaleAssay ? (
          <BroadscaleSamplesDetailTable assayId={broadscaleAssay.assay_id} orgId={orgId} studyId={studyId} />
        ) : (
          <Text>{emptyTxt}</Text>
        )}
      </Stack>
    </Stack>
  );
}
